import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  getDiscountDisplayAmountCents,
  getMatchingAppliedDiscountCents,
} from "../../../../utils/getDiscountDisplayAmountCents";
import { useGetBookingCharge_Booking$key } from "./__generated__/useGetBookingCharge_Booking.graphql";

type BookingCharge = {
  id: string;
  name: string;
  total: number;
};

type BookingDiscount = {
  id: string;
  name: string;
  appliedAmount: number;
  lineItemGroupId: string | null | undefined;
};

type BookingChargeGroup = {
  id: string;
  lineItems: BookingCharge[];
  discounts: BookingDiscount[];
};

export const useGetBookingCharge = ({
  bookingFragmentRef,
}: {
  bookingFragmentRef: useGetBookingCharge_Booking$key | null;
}) => {
  const data = useFragment(
    useGetBookingChargeFragmentString,
    bookingFragmentRef
  );

  const bookingLineItemGroupsByOrder =
    data?.orders?.edges?.map((order) =>
      (order?.node?.lineItemGroups ?? []).filter(
        (group) => group?.type === "BOOKING"
      )
    ) ?? [];

  const bookingLineItemGroupIds = new Set(
    bookingLineItemGroupsByOrder.flatMap((groups) =>
      groups.map((group) => group?.id).filter((id): id is string => Boolean(id))
    )
  );

  const bookingCharges: BookingCharge[] =
    bookingLineItemGroupsByOrder.flatMap((groups) => {
      const lineItems: BookingCharge[] = [];

      groups.forEach((group) => {
        group?.lineItems?.forEach((lineItem) => {
          const lineItemTotal = getBookingLineItemDisplayTotal(lineItem);

          if (
            !lineItem ||
            !lineItem.id ||
            !lineItem.name ||
            lineItemTotal == null ||
            lineItem.type !== "BOOKING"
          ) {
            return;
          }

          lineItems.push({
            id: lineItem.id,
            name: lineItem.name,
            total: lineItemTotal,
          });
        });
      });

      return lineItems;
    }) ?? [];

  const bookingDiscountsByLineItemGroupId =
    data?.orders?.edges?.reduce<Record<string, BookingDiscount[]>>(
      (accumulator, orderEdge) => {
        const discounts =
          orderEdge?.node?.orderDiscounts?.edges?.map(
            (discountEdge) => discountEdge?.node
          ) ?? [];
        const groupsById = new Map(
          (orderEdge?.node?.lineItemGroups ?? []).flatMap((group) => {
            if (!group?.id) {
              return [];
            }

            return [[group.id, group]];
          })
        );

        discounts.forEach((discount) => {
          if (
            !discount?.lineItemGroupId ||
            !bookingLineItemGroupIds.has(discount.lineItemGroupId)
          ) {
            return;
          }

          const existingDiscounts = accumulator[discount.lineItemGroupId] ?? [];
          const groupLineItems =
            groupsById.get(discount.lineItemGroupId)?.lineItems ?? [];

          accumulator[discount.lineItemGroupId] = [
            ...existingDiscounts,
            {
              id: discount.id,
              name: discount.name,
              appliedAmount: getDiscountDisplayAmountCents(
                discount,
                getMatchingAppliedDiscountCents(
                  discount.squareExternalId,
                  groupLineItems
                )
              ),
              lineItemGroupId: discount.lineItemGroupId,
            },
          ];
        });

        return accumulator;
      },
      {}
    ) ?? {};

  const bookingChargeGroups: BookingChargeGroup[] =
    bookingLineItemGroupsByOrder.flatMap((groups) => {
      return groups.flatMap((group) => {
        if (!group?.id) {
          return [];
        }

        const lineItems: BookingCharge[] = [];

        group?.lineItems?.forEach((lineItem) => {
          const lineItemTotal = getBookingLineItemDisplayTotal(lineItem);

          if (
            !lineItem ||
            !lineItem.id ||
            !lineItem.name ||
            lineItemTotal == null ||
            lineItem.type !== "BOOKING"
          ) {
            return;
          }

          lineItems.push({
            id: lineItem.id,
            name: lineItem.name,
            total: lineItemTotal,
          });
        });

        const discounts = bookingDiscountsByLineItemGroupId[group.id] ?? [];

        if (!lineItems.length && !discounts.length) {
          return [];
        }

        return [
          {
            id: group.id,
            lineItems,
            discounts,
          },
        ];
      });
    }) ?? [];

  const totalDiscountsForBooking = bookingChargeGroups.reduce(
    (acc, group) =>
      acc +
      group.discounts.reduce(
        (discountAccumulator, discount) =>
          discountAccumulator + (discount.appliedAmount ?? 0),
        0
      ),
    0
  );

  const bookingChargeTotal = bookingCharges.reduce(
    (acc, charge) => acc + charge.total,
    0
  );

  const bookingChargeTotalAfterDiscounts =
    bookingChargeTotal - totalDiscountsForBooking;

  return {
    bookingCharges,
    bookingChargeGroups,
    bookingChargeTotal: bookingChargeTotalAfterDiscounts,
  };
};

export const useGetBookingChargeFragmentString = graphql`
  fragment useGetBookingCharge_Booking on Booking {
    orders {
      edges {
        node {
          lineItemGroups {
            id
            type
            lineItems {
              id
              price
              quantity
              total
              name
              type
              ... on BookingLineItem {
                appliedDiscounts {
                  amountCents
                  squareExternalDiscountId
                }
              }
              ... on RoundLineItem {
                appliedDiscounts {
                  amountCents
                  squareExternalDiscountId
                }
              }
            }
          }
          orderDiscounts {
            edges {
              node {
                id
                name
                appliedAmount
                amount
                squareExternalId
                lineItemGroupId
              }
            }
          }
        }
      }
    }
  }
`;

const getBookingLineItemDisplayTotal = (
  lineItem:
    | {
        price?: number | null;
        quantity?: number | null;
        total?: number | null;
      }
    | null
    | undefined
) => {
  if (!lineItem) return null;

  if (lineItem.price != null && lineItem.quantity != null) {
    return lineItem.price * lineItem.quantity;
  }

  return lineItem.price ?? lineItem.total ?? null;
};
